import React, { useState, useContext } from 'react';
import { signInWithEmailAndPassword, sendPasswordResetEmail, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';
import { UserContext } from '../userContext';
import { useNavigate } from 'react-router-dom';
import { FaShieldAlt, FaGoogle, FaKey, FaUserPlus } from 'react-icons/fa';
import './Login.css';

function Login() {
  const { setUser } = useContext(UserContext);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const userData = userCredential.user;

      // Buscar dados extras no Firestore
      const userDoc = await getDoc(doc(db, "usuarios", userData.uid));
      let plano = 'Free'; // padrão
      if (userDoc.exists() && userDoc.data().plano) {
        plano = userDoc.data().plano;
      }

      setUser({
        name: userData.displayName || 'Usuário',
        email: userData.email,
        photoURL: userData.photoURL,
        uid: userData.uid,
        plano: plano,
      });

      navigate('/'); // Redirecionar para Home sem recarregar
    } catch (err) {
      console.error(err);
      if (err.code === 'auth/user-not-found') {
        setError('Usuário não encontrado. Verifique seu e-mail.');
      } else if (err.code === 'auth/wrong-password') {
        setError('Senha incorreta. Tente novamente.');
      } else if (err.code === 'auth/invalid-email') {
        setError('E-mail inválido. Verifique o formato.');
      } else {
        setError('Erro no login. Verifique seus dados e tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const userData = result.user;

      // Verificar se é um novo usuário e criar no Firestore
      const userDoc = await getDoc(doc(db, "usuarios", userData.uid));
      let plano = 'Free';
      
      if (!userDoc.exists()) {
        // Novo usuário - criar com plano gratuito
        await setDoc(doc(db, "usuarios", userData.uid), {
          name: userData.displayName,
          email: userData.email,
          plano: 'Free',
          createdAt: new Date(),
          provider: 'google'
        });
      } else {
        plano = userDoc.data().plano;
      }

      setUser({
        name: userData.displayName || 'Usuário',
        email: userData.email,
        photoURL: userData.photoURL,
        uid: userData.uid,
        plano: plano,
      });

      navigate('/');
    } catch (err) {
      console.error(err);
      setError('Erro no login com Google. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Digite seu e-mail primeiro para recuperar a senha.');
      return;
    }

    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await sendPasswordResetEmail(auth, email);
      setSuccess('E-mail de recuperação enviado! Verifique sua caixa de entrada.');
    } catch (err) {
      console.error(err);
      if (err.code === 'auth/user-not-found') {
        setError('E-mail não encontrado. Verifique se está correto.');
      } else {
        setError('Erro ao enviar e-mail de recuperação. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const goToRegister = () => {
    navigate('/cadastro');
  };

  return (
    <div className="login-container">
      <form onSubmit={handleLogin} className="login-form">
        <h2>Fazer Login</h2>
        
        {error && <p className="login-error">{error}</p>}
        {success && <p className="login-success">{success}</p>}
        
        <input
          type="email"
          placeholder="Seu e-mail"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={loading}
        />
        
        <input
          type="password"
          placeholder="Sua senha"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          disabled={loading}
        />
        
        <button 
          type="submit" 
          disabled={loading}
          className={loading ? 'loading' : ''}
        >
          {loading ? 'Entrando...' : 'Entrar'}
        </button>

        <div className="divider">
          <span>ou</span>
        </div>

        <button 
          type="button"
          className="google-button"
          onClick={handleGoogleLogin}
          disabled={loading}
        >
          <FaGoogle />
          Entrar com Google
        </button>
        
        <div className="security-badge">
          <FaShieldAlt />
          <span>Conexão segura e criptografada</span>
        </div>
        
        <div className="login-links">
          <button 
            type="button"
            className="login-link"
            onClick={handleForgotPassword}
            disabled={loading}
          >
            <FaKey />
            Esqueci minha senha
          </button>
          <button 
            type="button"
            className="login-link"
            onClick={goToRegister}
            disabled={loading}
          >
            <FaUserPlus />
            Criar conta
          </button>
        </div>
      </form>
    </div>
  );
}

export default Login;